import React, { useContext, useState } from 'react';
import { FlatList, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { TabbedContainerItem } from './TabbedContainerItem';
import CdnImageBackground from '../../common/CdnImageBackground';
import ComponentContainer from '../../common/ComponentContainer';
import ComponentTitle from '../../common/ComponentTitle';
import { Dropdown } from '../../common/Dropdown';
import { H6 } from '../../text';
import Utils from '../../../common/Utils';
import LayoutContext, { MODERN_LAYOUT_COLLAPSE_WIDTH } from '../../../contexts/Layout';

const Tab = ({ bottomMargin, handleTabPress, indent, isActive, title, textColor = '#fff', width }) => (
    <Pressable onPress={handleTabPress} style={{ paddingRight: indent }}>
        <H6
            style={{ color: textColor, fontWeight: isActive ? 'bold' : 'normal', marginBottom: bottomMargin }}
            text={width < MODERN_LAYOUT_COLLAPSE_WIDTH ? Utils?.truncateString(title, 22) : title}
        />
    </Pressable>
);

const TabbedContainer = ({
    backgroundImage,
    contentItems = {},
    enableImageOverlay = true,
    followContentLink = () => {},
    nodeTypeAlias = 'TabbedContainer',
    textColor,
    title,
    width,
    hardWidth
}) => {
    const { layout } = useContext(LayoutContext);
    const [imageDimensions, setImageDimensions] = useState();
    const contentItemsArray = Object.values(contentItems);
    const [active, setActive] = useState(() => contentItemsArray[0]?.id);
    const activeTab = contentItemsArray.find(tab => tab.id.equals(active));

    if (!activeTab) {
        return null;
    }

    const renderTab = (item, indent) => {
        const { key, rest } = Utils.splitKeyFromProps(item);
        return (
            <Tab
                {...rest}
                key={key}
                handleTabPress={() => setActive(item.id)}
                isActive={item.id === active}
                indent={indent}
                textColor={textColor}
                bottomMargin={layout.styleGuide.spacer3}
            />
        );
    };

    if (layout.device.isMobile || layout.device.isTablet) {
        const items = contentItemsArray.map(tab => {
            return {
                key: tab.id,
                label: tab.title,
                value: tab.id
            };
        });

        return (
            <CdnImageBackground
                enableOverlay={enableImageOverlay}
                imageDimensions={imageDimensions}
                setImageDimensions={setImageDimensions}
                uri={backgroundImage.cdnImagePath}
                style={{ height: undefined }}
                width={hardWidth || width}
                dataSet={{ umbracoComponentName: nodeTypeAlias }}
            >
                <ComponentContainer style={{ backgroundColor: 'transparent', height: undefined }}>
                    <View style={{ borderBottomColor: textColor, borderBottomWidth: StyleSheet.hairlineWidth, flexDirection: 'row', marginTop: 10 }}>
                        <Dropdown
                            iconColor={textColor}
                            includeSelection={false}
                            label={activeTab.title}
                            labelStyle={{ color: textColor }}
                            onSelect={val => setActive(val)}
                            options={items}
                            selection={active}
                        />
                    </View>
                    <View style={{ alignItems: 'center', justifyContent: 'center', marginHorizontal: layout.styleGuide.spacer1 }}>
                        <TabbedContainerItem
                            followContentLink={followContentLink}
                            {...Utils.splitKeyFromProps(Object.values(activeTab?.contentItems ?? {})[0]).rest}
                            width={width}
                        />
                    </View>
                </ComponentContainer>
            </CdnImageBackground>
        );
    }

    return (
        <CdnImageBackground
            enableOverlay={enableImageOverlay}
            imageDimensions={imageDimensions}
            setImageDimensions={setImageDimensions}
            style={{ justifyContent: 'center', marginBottom: layout.styleGuide.componentSeparator }}
            uri={backgroundImage.cdnImagePath}
            width={hardWidth || width}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <View style={{ marginHorizontal: layout.styleGuide.spacer2 }}>
                {!!title && <ComponentTitle style={{ color: textColor }} text={title} />}
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ alignContent: 'center', alignItems: 'flex-end', justifyContent: 'center' }}>
                        {contentItemsArray.map(item => renderTab(item, renderTab(item, layout.styleGuide.indent)))}
                    </View>
                    <View style={{ borderLeftWidth: 1, borderLeftColor: 'white', alignItems: 'center', justifyContent: 'center', marginHorizontal: layout.styleGuide.indent }}>
                        <TabbedContainerItem
                            followContentLink={followContentLink}
                            {...Utils.splitKeyFromProps(Object.values(activeTab?.contentItems ?? {})[0]).rest}
                            width={Math.floor(hardWidth)}
                        />
                    </View>
                </View>
            </View>
        </CdnImageBackground>
    );
};

TabbedContainer.propTypes = {
    backgroundImage: PropTypes.shape({
        cdnImagePath: PropTypes.string
    }),
    enableImageOverlay: PropTypes.bool,
    nodeName: PropTypes.string,
    textColor: PropTypes.string,
    title: PropTypes.string,
    contentItems: PropTypes.object
};

export default TabbedContainer;
